import { roleAtLeast, type Role, type Section } from '../audience';
import type { Lang, UserStatus } from './auth';

/** Directory entry. Contact fields are only present when shared (or the viewer is admin). */
export interface MemberDTO {
  id: string;
  fullName: string;
  section: Section | null;
  role: Role;
  title: string | null;
  status: UserStatus;
  avatarRev: number;
  phone: string | null;
  email: string | null;
  joinedOn: string;
}

/** Admin view of a member (GET /api/admin/members). */
export interface AdminMemberDTO extends MemberDTO {
  loginEmail: string;
  mustOnboard: boolean;
  hasPassword: boolean;
  lastSeen: number | null;
  sharePhone: boolean;
  shareEmail: boolean;
}

/** POST /api/admin/members (admin). id is a client UUID. */
export interface CreateMemberRequest {
  id: string;
  email: string;
  fullName: string;
  section: Section | null;
  role: Role;
  title?: string | null;
}

/** PATCH /api/members/:id — editors may change section/title; only admins role/status/email/name. */
export interface UpdateMemberRequest {
  fullName?: string;
  email?: string;
  section?: Section | null;
  role?: Role;
  title?: string | null;
  status?: 'active' | 'leave' | 'inactive';
}

/** POST /api/admin/members/:id/link { kind } → one-time link (invite or password reset). */
export interface MemberLinkRequest {
  kind: 'invite' | 'reset';
}
export interface MemberLinkResponse {
  url: string;
  expiresAt: number;
}

/**
 * Invite links by voice (editors and admins): only the section and an optional title are chosen; the
 * person who opens the link (/#/skraning/<token>) fills in their own name, e-mail and phone and chooses
 * a password (POST /api/auth/signup, see dto/auth.ts). Single use, valid for 7 days; the new member gets
 * role 'member' (an admin may change it afterwards).
 *   GET    /api/admin/signup-invites       SignupInviteDTO[]: pending links (unused, unexpired), newest first
 *   POST   /api/admin/signup-invites       CreateSignupInviteRequest → 201 CreateSignupInviteResponse
 *   DELETE /api/admin/signup-invites/:id   revoke a pending link → 204 (404 when used, expired or unknown)
 * The link itself is only in the POST response: the server keeps a hash of the token.
 */
export interface SignupInviteDTO {
  id: string;
  section: Section;
  title: string | null;
  createdBy: { id: string; fullName: string };
  createdAt: number;
  expiresAt: number;
}
export interface CreateSignupInviteRequest {
  section: Section;
  title?: string | null;
}
export interface CreateSignupInviteResponse extends SignupInviteDTO {
  url: string;
}

/** PATCH /api/profile — the signed-in user's own profile. */
export interface ProfileUpdateRequest {
  fullName?: string;
  phone?: string | null;
  emailPublic?: string | null;
  sharePhone?: boolean;
  shareEmail?: boolean;
  lang?: Lang;
  mustOnboard?: false;
}

/**
 * PATCH /api/members/:id response: AdminMemberDTO when the caller is admin, MemberDTO otherwise.
 *
 * Changing a member's login e-mail clears the password (its salt depends on the e-mail), signs the
 * member out everywhere and cancels every outstanding invite/reset link (they were sent to the old
 * address). The member then needs a NEW link, which the admin should create right away:
 *   `requiresReset`  member can sign in (active/leave) → POST /api/admin/members/:id/link { kind: 'reset' }
 *   `requiresInvite` member is still invited            → POST /api/admin/members/:id/link { kind: 'invite' }
 * (Neither is set for an inactive member: links are only made after /reactivate.)
 */
export type UpdateMemberResponse = (MemberDTO | AdminMemberDTO) & { requiresReset?: boolean; requiresInvite?: boolean };

/**
 * Refusal reasons of the members/profile/avatar/admin routes. They arrive as `error.message` next to a
 * generic `error.code` ('forbidden' 403 or 'conflict' 409), so clients may branch on them:
 *
 *   reason             status  endpoint(s)
 *   email_taken        409     PATCH /members/:id (e-mail change), POST /admin/members, POST /auth/signup
 *   id_taken           409     POST /admin/members (same id, different e-mail)
 *   own_role           403     PATCH /members/:id — nobody changes their own role
 *   own_status         403     PATCH /members/:id { status: 'inactive' }, POST /admin/members/:id/deactivate — not yourself
 *   own_email          403     PATCH /members/:id — an admin cannot change their own login e-mail (it clears the password)
 *   invite_pending     409     PATCH /members/:id { status: 'active'|'leave' } on an invited member — they sign up via an invite link
 *   use_reactivate     409     PATCH /members/:id { status: 'active'|'leave' } on an inactive member — use POST /admin/members/:id/reactivate
 *   stale_avatar_rev   409     PUT /avatar/:rev/:size, POST /avatar/commit (and the same under /members/:id/avatar) —
 *                              rev must be the next revision; details: StaleAvatarRevDetails { nextRev }
 *   avatar_incomplete  409     POST /avatar/commit, POST /members/:id/avatar/commit — both sizes must have been
 *                              uploaded for this rev within the last hour
 *   not_invited        409     POST /admin/members/:id/link { kind: 'invite' } — member is not in status 'invited'
 *   not_active         409     POST /admin/members/:id/link { kind: 'reset' } — member is not active/on leave
 *   not_inactive       409     POST /admin/members/:id/erase — deactivate first
 *
 * ('last_admin' is a top-level error code, see shared/errors.ts.)
 */
export const MEMBER_ERROR_REASONS = [
  'email_taken',
  'id_taken',
  'own_role',
  'own_status',
  'own_email',
  'invite_pending',
  'use_reactivate',
  'stale_avatar_rev',
  'avatar_incomplete',
  'not_invited',
  'not_active',
  'not_inactive',
] as const;
export type MemberErrorReason = (typeof MEMBER_ERROR_REASONS)[number];

/**
 * POST /api/avatar/commit — after PUT /api/avatar/:rev/512 and /:rev/128 (raw JPEG).
 * rev = the next revision: me.avatarRev + 1, except after DELETE /api/avatar (avatarRev 0): revision
 * numbers are never reused (avatar URLs are cached as immutable), so the server then expects a higher
 * one and answers 409 stale_avatar_rev with StaleAvatarRevDetails — retry with `nextRev`.
 */
export interface AvatarCommitRequest {
  rev: number;
}

/** `error.details` of 409 stale_avatar_rev (PUT /api/avatar/:rev/:size, POST /api/avatar/commit and the same under /api/members/:id/avatar). */
export interface StaleAvatarRevDetails {
  /** The only revision the server accepts right now. */
  nextRev: number;
}

/**
 * Another member's photo (editors and admins), same protocol as the own photo above:
 *   PUT    /api/members/:id/avatar/:rev/:size   raw JPEG (≤ 1 MB), size 512|128, rev = the next revision
 *   POST   /api/members/:id/avatar/commit       AvatarCommitRequest → MemberAvatarResponse
 *   DELETE /api/members/:id/avatar              → MemberAvatarResponse
 * 403 for members (they change only their own photo, via /api/avatar — also one someone else set);
 * 404 for unknown and erased members, and for inactive ones unless the caller is an admin
 * (canManageAvatarOf). Every change of another member's photo is written to audit_log as
 * member_avatar_added | member_avatar_replaced | member_avatar_removed (details { rev: { from, to } }).
 */
export type MemberAvatarResponse = MemberDTO | AdminMemberDTO;

/**
 * May `actor` add, replace or remove the photo of `target` through /api/members/:id/avatar?
 * Editors (the conductor) and admins, for every member who is not erased; inactive members only
 * admins — the same members PATCH /api/members/:id accepts. The server enforces this; the client
 * uses it to show or hide the photo buttons.
 */
export function canManageAvatarOf(actor: { role: Role }, target: { status: UserStatus }): boolean {
  if (!roleAtLeast(actor.role, 'editor') || target.status === 'erased') return false;
  return target.status !== 'inactive' || actor.role === 'admin';
}

/** GET /api/admin/members/:id/export — GDPR access request (JSON attachment). */
export interface MemberExportDTO {
  exportedAt: number;
  profile: {
    id: string;
    loginEmail: string;
    fullName: string;
    section: Section | null;
    role: Role;
    title: string | null;
    status: UserStatus;
    phone: string | null;
    emailPublic: string | null;
    sharePhone: boolean;
    shareEmail: boolean;
    avatarRev: number;
    lang: Lang;
    mustOnboard: boolean;
    hasPassword: boolean;
    joinedOn: string;
    createdAt: number;
    updatedAt: number;
  };
  sessions: { createdAt: number; lastSeen: number; expiresAt: number; userAgent: string | null }[];
  participation: {
    eventId: string;
    eventTitle: string | null;
    eventStartsAt: number | null;
    rsvp: string | null;
    note: string | null;
    rsvpAt: number | null;
    checkedInAt: number | null;
    method: string | null;
    override: string | null;
    updatedAt: number;
  }[];
  comments: {
    id: string;
    eventId: string;
    replyTo: string | null;
    body: string;
    createdAt: number;
    editedAt: number | null;
    deletedAt: number | null;
  }[];
  /** Messages in the group chats (shared/chats.ts), deleted ones included. */
  chatMessages: {
    id: string;
    chatId: string;
    replyTo: string | null;
    body: string;
    createdAt: number;
    editedAt: number | null;
    deletedAt: number | null;
  }[];
  layers: { id: string; sheetFileId: string; name: string; shared: boolean; createdAt: number; deletedAt: number | null }[];
  audit: { at: number; action: string; details: unknown }[];
}

// ───────────── System (owned by the members/auth server team; no separate DTO file) ─────────────

/** POST /api/client-errors (signed-in users) → 204. At most 10 errors per request. */
export interface ClientErrorReport {
  message: string;
  route?: string;
  code?: string;
}
export interface ClientErrorsRequest {
  errors: ClientErrorReport[];
}

/** GET /api/admin/errors (admin) → ErrorLogEntryDTO[] (newest first, max 200). */
export interface ErrorLogEntryDTO {
  id: number;
  at: number;
  source: 'server' | 'client';
  route: string | null;
  code: string | null;
  message: string | null;
  userId: string | null;
  userName: string | null;
  ua: string | null;
}

/** POST /api/admin/housekeeping (editor) → bounded cleanup counts. */
export interface HousekeepingResponse {
  deleted: {
    sessions: number;
    authTokens: number;
    /** Used or expired invite links by voice (signup_invites). */
    signupInvites: number;
    throttle: number;
    trash: number;
    annotationTombstones: number;
  };
}
